import React, { useEffect, useState } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { Form, Formik } from "formik";
import * as Yup from "yup";

import { getSupers, createSuper, deleteSuper, updateUser } from "api/Users";

import { Modal, ModalBody, ModalHeader, ModalFooter } from "components/ui/Modal";
import Header from "components/ui/Header";
import TableData from "components/ui/TableData";
import Page from "components/ui/Page";
import BasicButton from "components/forms/BasicButton";
import TextInput from "components/forms/TextInput";
import { useToast } from "components/toast";
import BasicModal from "components/ui/BasicModal";
import P from "components/typography/P";
import PlusIcon from "components/svg/icons/PlusIcon";
import { Box, Flex, Grid } from "theme-ui";

export default function Supers() {

  const toast = useToast();  
  const [refreshData, setRefreshData] = useState(false);  

  const [editModal, toggleEditModal] = useState<boolean>(false);
  const [createModal, toggleCreateModal] = useState<boolean>(false);

  const [userInfo, setUserInfo] = useState<any>(null);  

  const [confirmModal, setConfirmModal] = useState<any>(false);
  const [confirmSubmit, setConfirmSubmit] = useState<boolean>(false);

  const columnHelper = createColumnHelper();
  const columns = [
    columnHelper.accessor("firstName", {
      id: "firstName",
      cell: (info) => (
        <div onClick={() => setEditModal(info.row.original)}>
          {info.getValue()}
        </div>
      ),
      header: () => <span>First Name</span>,
    }),
    columnHelper.accessor("lastName", {
      id: "lastName",
      cell: (info) => (
        <div onClick={() => setEditModal(info.row.original)}>
          {info.getValue()}
        </div>
      ),
      header: () => <span>Last Name</span>,
    }),
    columnHelper.accessor("email", {
      id: "email",
      cell: (info) => (
        <div onClick={() => setEditModal(info.row.original)}>
          {info.getValue()}
        </div>
      ),
      header: () => <span>Email</span>,
    }),
    columnHelper.accessor("status", {
      id: "status",
      cell: (info) => (        
        <div onClick={() => setEditModal(info.row.original)}>
            {(() => {
              switch(info.getValue()){
                case(1):
                  return "Active";                     
                default:
                  return "Inactive";            
              }
            })()}
        </div>
      ),     
      header: () => <span>Status</span>,
    }),
  ];                    

  const setEditModal = (the_user: any) => {      
    if(the_user){
      setUserInfo(the_user);
      toggleEditModal(true);
    } else {
      setUserInfo(null);
      toggleEditModal(false);
    }
  };  

  const UserSchema = Yup.object().shape({
    email: Yup.string().email("Invalid email").required("Email is required."),
    firstName: Yup.string().required("First Name is required."),
    lastName: Yup.string().required("Last Name is required."),   
  }).noUnknown(true);

  const submitUserUpdate = async (values: any, actions: any) => {   
    const update = {
      firstName : values.firstName,
      lastName: values.lastName,
      email: values.email,
    }
    
    const res = await updateUser({ data : userInfo, update });
    if(res?.firstName){ 
      toast.add("User updated","var(--theme-ui-colors-green)");
      setRefreshData(true);
      setTimeout(() => { setRefreshData(false) }, 100);   
    }else{
      toast.add(res?.message ? res.message : "Error updating user","var(--theme-ui-colors-red)");
    }    
    toggleEditModal(false);        
  }; 

  const submitCreateUser = async (values: any, { setErrors}: any) => {
  
    const create = {
      email: values.email,
      firstName: values.firstName,
      lastName: values.lastName,
    };

    const res = await createSuper({ create });
    if(res?.success){
      toast.add(res?.message ? res.message : "User created","var(--theme-ui-colors-green)");
      toggleCreateModal(false);    
      setRefreshData(true);
      setTimeout(() => { setRefreshData(false) }, 100);   
    } else {  
      toast.add(res?.message ? res.message : "Error created user","var(--theme-ui-colors-red)");
    }
  }; 

  const CreateSchema = Yup.object().shape({
    email: Yup.string().email("Invalid email").required("Email is required."),
    firstName: Yup.string().required("First Name is required."),
    lastName: Yup.string().required("Last Name is required."),    
  });  

  const confirmDeleteUser = async () => { 
    setConfirmSubmit(true);   
    const res = await deleteSuper({ data: userInfo });
    if(res?.success){ 
      toast.add(res?.message ? res.message : "User deleted","var(--theme-ui-colors-green)");
      setRefreshData(true);    
      setTimeout(() => { setRefreshData(false) }, 100);   
    }else{
      toast.add(res?.message ? res.message : "Error deleting user","var(--theme-ui-colors-red)");
    } 
    setConfirmSubmit(false);   
    setConfirmModal(false)          
  };  
  
  const cancelDeleteUser = async () => {
    setConfirmModal(false)
  }

  const submitDeleteUser = async () => {
    toggleEditModal(false);  
    setConfirmModal(<BasicModal title="Delete User?" confirm_click={confirmDeleteUser} cancel_click={cancelDeleteUser} $submitting={confirmSubmit}>
      <P>Are you sure you want to delete <strong>{userInfo?.firstName} {userInfo?.lastName}</strong>?</P>
    </BasicModal>);
  }

  const toggleEdit = () => toggleEditModal(!editModal);
  const toggleCreate = () => toggleCreateModal(!createModal);

  useEffect(() => {
    document.title = process.env.REACT_APP_NAME + " | Users"
  }, []);

  return (
    <>
      <Header></Header>      
      <Page title="Users" subtitle="Manage user access.">                
        <TableData 
          title={"All Users"}
          api={getSupers} 
          columns={columns} 
          refresh={refreshData} 
          actions={[
            <BasicButton 
              key="1"
              scheme="primary"              
              onClick={toggleCreate}
              $iconHeight="10px" 
            ><PlusIcon/>Add user</BasicButton>,            
          ]} 
        />
      </Page>
      <Modal isOpen={editModal} toggle={toggleEdit} size="lg" centered={true}>
        <Formik initialValues={{...userInfo }} onSubmit={submitUserUpdate} validationSchema={UserSchema} >
          {({ isSubmitting, errors, values, submitCount, setFieldValue }: any) => { 
            return (                
            <Form noValidate autoComplete="off">
              <ModalHeader toggle={toggleEdit}>Edit User</ModalHeader>
              <ModalBody> 
                <Grid gap={3} columns={[1,2]}>                  
                  <TextInput 
                    name="firstName"
                    label="First Name"
                    value={values.firstName}
                    onChange={(val: any) => setFieldValue("firstName",val)}
                    $errors={errors.firstName && submitCount > 0 ? errors.firstName : null}                                    
                  />                   
                  <TextInput 
                    name="lastName"
                    label="Last Name"
                    value={values.lastName}
                    onChange={(val: any) => setFieldValue("lastName",val)}
                    $errors={errors.lastName && submitCount > 0 ? errors.lastName : null}                                    
                  />                   
                  <TextInput 
                    type="email" 
                    name="email" 
                    label="Email"
                    value={values.email}
                    onChange={(val: any) => setFieldValue("email",val)}
                    $errors={errors.email && submitCount > 0 ? errors.email : null}                                    
                  />                  
                </Grid>                                                                       
              </ModalBody>
              <ModalFooter>
                <Flex sx={{display: "flex", flexWrap : "wrap-reverse", gap : "20px", justifyContent : "space-between", width : "100%"}}>
                  <Box>
                    <BasicButton 
                      scheme="danger"
                      outline={true}
                      type="button" 
                      onClick={submitDeleteUser} 
                    >Delete</BasicButton> 
                  </Box>
                  <Flex sx={{display: "flex", flexWrap : "wrap-reverse", gap : "20px",}}>
                    <BasicButton 
                      outline={true}                  
                      type="button" 
                      onClick={toggleEdit} 
                    >Cancel</BasicButton>   
                    <BasicButton 
                      scheme="secondary"                     
                      outline={true}
                      type="submit" 
                      $submitting={isSubmitting}
                    >Update User</BasicButton>
                  </Flex>
                </Flex>                
              </ModalFooter>
            </Form>
            );
          }}
        </Formik>
      </Modal>
      <Modal isOpen={createModal} toggle={toggleCreate} centered={true}>
        <Formik initialValues={{email:"",role: null ,firstName:"",lastName:""}} onSubmit={submitCreateUser} validationSchema={CreateSchema} >
          {({ isSubmitting, errors, values, submitCount, setFieldValue }) => {
            return (              
            <Form noValidate autoComplete="off">
              <ModalHeader toggle={toggleCreate}>Add User</ModalHeader>
              <ModalBody> 
                <Grid gap={3} columns={[1,2]}>                                
                  <TextInput
                    type="email" 
                    name="email" 
                    label="Email"  
                    value={values.email}
                    onChange={(val: any) => setFieldValue("email",val)}
                    autoComplete="off"
                    $errors={errors.email && submitCount > 0 ? errors.email : null}  
                  />                  
                  <TextInput 
                    name="firstName" 
                    label="First Name" 
                    value={values.firstName}
                    onChange={(val: any) => setFieldValue("firstName",val)}
                    autoComplete="off"
                    $errors={errors.firstName && submitCount > 0 ? errors.firstName : null}  
                  />
                  <TextInput 
                    name="lastName" 
                    label="Last Name" 
                    value={values.lastName}
                    onChange={(val: any) => setFieldValue("lastName",val)}
                    autoComplete="off"
                    $errors={errors.lastName && submitCount > 0 ? errors.lastName : null} 
                  />                                                                
                </Grid>
              </ModalBody>
              <ModalFooter>
                <BasicButton 
                  outline={true}                
                  type="button" 
                  onClick={toggleCreate} 
                >Cancel</BasicButton>
                <BasicButton 
                  scheme="secondary"          
                  type="submit" 
                  $submitting={isSubmitting}
                >Create</BasicButton>                
              </ModalFooter>
            </Form>
          );
        }}            
        </Formik>
      </Modal>
      {confirmModal}      
    </>
  );
}